// src/utils/sendOnWhatsApp.js
// Sends a WhatsApp message via the backend gateway (superadmin auth required).
// Usage:
//   import { sendOnWhatsApp } from '../../utils/sendOnWhatsApp';
//   await sendOnWhatsApp({ mobileRaw: '9876543210', message: 'Hello!', token });

const DEFAULT_API_URL = `${process.env.REACT_APP_API_URL || ''}/api/superadmin/whatsapp/send`;

function onlyDigits(v) {
  return String(v || '').replace(/\D/g, '');
}

/**
 * Normalize to international format.
 * "9876543210" → "919876543210"
 * "+91 98765 43210" → "919876543210"
 * "919876543210" (already with code) → "919876543210"
 */
export function normalizeMobileWithCountryCode(mobileRaw, countryCode = '91') {
  const d = onlyDigits(mobileRaw);
  if (!d) return '';
  if (d.length === 10) return `${countryCode}${d}`;
  return d; // already includes country code or longer
}

/**
 * Send a WhatsApp message via the project backend.
 *
 * @param {object} opts
 * @param {string}  opts.mobileRaw   - Mobile number (any format)
 * @param {string}  opts.message     - Message text
 * @param {string}  opts.token       - JWT auth token (localStorage 'ep_token')
 * @param {string}  [opts.apiUrl]    - Override API endpoint
 * @param {string}  [opts.countryCode] - Default "91" (India)
 */
export async function sendOnWhatsApp({
  mobileRaw,
  message,
  token,
  apiUrl = DEFAULT_API_URL,
  countryCode = '91',
}) {
  const mobile = normalizeMobileWithCountryCode(mobileRaw, countryCode);
  if (!mobile) throw new Error('Mobile number not found');

  console.log('[WA] →', mobile, message.slice(0, 80));

  const res = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ mobile, message }),
  });

  const bodyText = await res.text().catch(() => '');
  console.log('[WA] status:', res.status, bodyText);

  if (!res.ok) throw new Error(`WhatsApp API failed: ${res.status} ${bodyText}`);
  return bodyText;
}

/**
 * Build a gallery-ready message for a visitor.
 */
export function buildGalleryMessage({ visitorName, eventName, galleryUrl }) {
  return (
    `Hi ${visitorName || 'there'}! 🎉\n\n` +
    `Your photos from *"${eventName || 'the event'}"* are ready!\n\n` +
    `📸 View & download your photos:\n${galleryUrl}\n\n` +
    `— Smart Click Memories`
  );
}

/**
 * Build an event registration message (broadcast to all visitors).
 */
export function buildEventInviteMessage({ eventName, registerUrl }) {
  return (
    `📸 *Smart Click Memories*\n\n` +
    `Join *"${eventName || 'our event'}"* and find your photos!\n\n` +
    `Register here:\n${registerUrl}\n\n` +
    `Upload your selfie after registering to see all your matched photos.`
  );
}
